import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { AddonInterface } from "@ts/interfaces/Checkout/AddonInterface";
import {
    CheckoutPackage,
    PackageFrequency
} from "@ts/interfaces/Checkout/CheckoutPackageInterface";
import { GradeInterface } from "@ts/interfaces/Checkout/GradeInterface";
import { PlanInterface } from "@ts/interfaces/Checkout/PlanInterface";
import { ProductFamilyInterface } from "@ts/interfaces/Checkout/ProductFamilyInterface";

import {
    getLearningModeAddons,
    hasLearningModeOptions
} from "@utils/learningMode";
import { getPlansForTargetGrade } from "@utils/utils";

/**
 * Combined "package" selection model (Guided/Assessed Programs).
 *
 * When a grade's plans expose learning-mode add-ons (Self-Directed + Guided),
 * the checkout shows a single page that bundles every (frequency × learning
 * mode) permutation behind a Yearly/Term toggle. Learning modes are ONLY
 * selectable there: a grade without complete learning-mode metadata uses the
 * standard membership page and never carries a learning mode.
 *
 * A "package" is one plan paired with one of that plan's learning-mode add-ons:
 *   - Self-Directed → the $0 learning-mode add-on; total = plan price.
 *   - Guided        → the paid learning-mode add-on; total = plan + add-on.
 *
 * All helpers here are pure so they can be unit tested without React.
 */

/**
 * Billing frequency of a plan, derived the same way as the membership page:
 *   - `period: "year"` with a single instalment → yearly
 *   - `period: "month"` → term (billed every few months)
 * Instalment ("year" with >1 instalment) and any other shape are treated as
 * term for the toggle, but in practice guided grades only have yearly + term
 * plans.
 */
export const getPackageFrequency = (plan: PlanInterface): PackageFrequency =>
    plan.period === "year" && plan.numberOfInstalments === 1
        ? "yearly"
        : "term";

/**
 * True when every plan for the grade exposes learning-mode options, i.e. the
 * combined package page should be used in place of the membership page. A
 * grade with any plan lacking learning modes (mixed/incomplete metadata) fails
 * closed to the standard membership flow without learning modes.
 */
export const hasPackageOptions = (
    plans: PlanInterface[] | null | undefined
): boolean =>
    !!plans &&
    plans.length > 0 &&
    plans.every(p => hasLearningModeOptions(p.addons));

/**
 * Plans offered for the selected grade, falling back to every family plan when
 * no plan targets the grade (or no grade is selected yet).
 */
export const getProgramPlans = (
    productFamily: ProductFamilyInterface | null | undefined,
    grade: GradeInterface | null | undefined
): PlanInterface[] | undefined =>
    productFamily && grade
        ? getPlansForTargetGrade(productFamily.plans, grade.name)
        : productFamily?.plans;

/** True when the program's grade selects plans on the combined package page. */
export const usesPackageSelection = (
    productFamily: ProductFamilyInterface | null | undefined,
    grade: GradeInterface | null | undefined
): boolean => hasPackageOptions(getProgramPlans(productFamily, grade));

/**
 * Route a plan-selection step should navigate to for the given grade's plans:
 * the combined package page when guided options exist, otherwise the standard
 * membership page. Centralised so every entry point stays consistent.
 */
export const getPlanSelectionRoute = (
    plans: PlanInterface[] | null | undefined
): CheckoutPageRoutesEnum =>
    hasPackageOptions(plans)
        ? CheckoutPageRoutesEnum.PACKAGE_SELECT_PAGE
        : CheckoutPageRoutesEnum.PLAN_SELECT_PAGE;

const packageId = (plan: PlanInterface, learningMode: AddonInterface) =>
    `${plan.id}::${learningMode.id}`;

/**
 * Per-instalment total of the term package that matches a yearly package's
 * learning mode, multiplied by the term plan's instalment count — i.e. what a
 * family would pay across a year on the term plan. Used as the yearly card's
 * crossed-out "was" price. Returns null when there's no term plan or no
 * matching learning mode on it.
 */
const deriveYearlyCrossedPrice = (
    isGuided: boolean,
    termPlan: PlanInterface | undefined
): number | null => {
    if (!termPlan) {
        return null;
    }

    const termModes = getLearningModeAddons(termPlan.addons);
    const termMode = isGuided
        ? termModes.find(a => a.price > 0)
        : termModes.find(a => a.price <= 0);

    if (!termMode) {
        return null;
    }

    const instalments = termPlan.numberOfInstalments || 1;
    const termTotal = termPlan.price + (isGuided ? termMode.price : 0);

    return termTotal * instalments;
};

/**
 * Build every (frequency × learning mode) package for a grade's plans.
 * Returns an empty array when the grade has no learning-mode options.
 */
export const buildPackages = (
    plans: PlanInterface[] | null | undefined
): CheckoutPackage[] => {
    if (!hasPackageOptions(plans) || !plans) {
        return [];
    }

    const termPlan = plans.find(p => getPackageFrequency(p) === "term");

    return plans.flatMap(plan => {
        const frequency = getPackageFrequency(plan);
        const learningModes = getLearningModeAddons(plan.addons);

        return learningModes.map<CheckoutPackage>(learningMode => {
            const isGuided = learningMode.price > 0;
            const totalPrice = plan.price + (isGuided ? learningMode.price : 0);

            return {
                id: packageId(plan, learningMode),
                frequency,
                isGuided,
                plan,
                learningMode,
                totalPrice,
                crossedPrice:
                    frequency === "yearly"
                        ? deriveYearlyCrossedPrice(isGuided, termPlan)
                        : null,
                currency: plan.currency
            };
        });
    });
};

/** Packages for one frequency, sorted Self-Directed first then Guided. */
export const getPackagesForFrequency = (
    packages: CheckoutPackage[],
    frequency: PackageFrequency
): CheckoutPackage[] =>
    packages
        .filter(p => p.frequency === frequency)
        .sort((a, b) => Number(a.isGuided) - Number(b.isGuided));

/** True when the grade has at least one yearly and one term package. */
export const hasBothFrequencies = (packages: CheckoutPackage[]): boolean =>
    packages.some(p => p.frequency === "yearly") &&
    packages.some(p => p.frequency === "term");

/**
 * Largest saving (as a whole-number percentage) across the yearly packages,
 * powering the "YEARLY (SAVE UP TO X%)" toggle label. Returns null when no
 * yearly package has a usable crossed price.
 */
export const getMaxYearlySavingPercent = (
    packages: CheckoutPackage[]
): number | null => {
    const savings = packages
        .filter(p => p.frequency === "yearly")
        .map(p =>
            p.crossedPrice && p.crossedPrice > p.totalPrice
                ? Math.floor((1 - p.totalPrice / p.crossedPrice) * 100)
                : null
        )
        .filter((v): v is number => v !== null && v > 0);

    return savings.length > 0 ? Math.max(...savings) : null;
};
